import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSubscription } from '../../../hooks/useSubscription';
import { queryKeys, useHeartbeatRun as useHeartbeatRunQuery } from '../../../services/queries';
import { ACTIVE } from './pageState';
import { invalidateHeartbeatReading, reviewIsActive } from './useHeartbeatContext';

/**
 * Live run state: the REST snapshot, updated in place by heartbeat.run.update
 * frames, with a 5s poll while active in case the socket is down. On a
 * terminal status the account's timeline/grid/runs are invalidated once.
 */
export function useHeartbeatRun(runId, accountId, revision) {
	const qc = useQueryClient();
	// The poll lives on React Query's observer, not in a useEffect interval.
	// The previous effect depended on `query`, which useQuery returns as a fresh
	// Proxy on EVERY render - so the interval was cleared and recreated on every
	// re-render, and any re-render cadence under 5s meant it never fired once.
	// A flapping socket re-renders this page through WebSocketContext at roughly
	// 3s, which is exactly the situation the poll existed to cover: the fallback
	// was disabled precisely when it was needed.
	//
	// The function form reads status off the query itself, so it does not depend
	// on a `status` computed after this call.
	const query = useHeartbeatRunQuery(runId, {
		revision,
		refetchInterval: (q) => {
			if (revision) return false;
			const s = q.state.data?.status;
			return !s || ACTIVE.includes(s) || reviewIsActive(q.state.data?.contextReview) ? 5000 : false;
		},
		refetchIntervalInBackground: false,
	});
	const status = query.data?.status;
	const isActive = !!runId && (!status || ACTIVE.includes(status));

	const onMessage = useCallback((message) => {
		if (message?.type !== 'heartbeat.run.update' || !message.payload) return;
		if (revision) return;
		qc.setQueryData(queryKeys.heartbeatRun(runId), (prev) => ({ ...(prev || {}), ...message.payload }));
	}, [qc, runId, revision]);

	useSubscription(runId ? `heartbeat:${runId}` : null, onMessage, { enabled: !!runId });

	useEffect(() => {
		if (status && !ACTIVE.includes(status) && accountId) {
			qc.invalidateQueries({ queryKey: ['heartbeat', accountId] });
		}
	}, [status, accountId, qc]);
	const reviewStatus = query.data?.contextReview?.status;
	const readingRevision = query.data?.readingRevision;
	useEffect(() => {
		if (!revision && accountId && readingRevision && reviewStatus === 'ready') {
			invalidateHeartbeatReading(qc, accountId);
		}
	}, [revision, accountId, qc, readingRevision, reviewStatus]);

	return { run: query.data || null, isActive, isLoading: query.isLoading, error: query.error };
}
